"use client";
import { cn } from "@scott/ui/client";
import type { BrandConfig, isSlotsData } from "@scott/ui-d2c/types";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";
import { Body } from "../../../../packages/ui-d2c/src/components/ui/body";
import { Button } from "../../../../packages/ui-d2c/src/components/ui/button/button";
import { Heading } from "../../../../packages/ui-d2c/src/components/ui/heading";
import { Input } from "../../../../packages/ui-d2c/src/components/ui/input";
import { encryptEmail } from "../../lib/server/actions/safe-url-params";

export interface footerCTAProps {
  brand: BrandConfig;
  className?: string;
  slotsData?: isSlotsData[];
}

const FooterCTA = ({ brand, className, slotsData }: footerCTAProps) => {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");

  const handleEmailChange = (value: string) => {
    setEmail(value);
  };

  const handleSubmit = async (submitLink: string) => {
    if (inputRef.current && !inputRef.current.checkValidity()) {
      inputRef.current.reportValidity();
      return;
    }

    let url = submitLink;
    if (email) {
      const encryptedEmail = await encryptEmail(email);
      url = `${submitLink}${submitLink.includes("?") ? "&" : "?"}email=${encryptedEmail}`;
    }
    router.push(url);
  };

  return (
    <div className={cn(className, "bg-neutral-lightest")}>
      <div
        className={cn(
          "container flex flex-row text-neutral-dark",
          brand.isScott && "gap-4 py-2"
        )}
      >
        {slotsData?.map((slot, index) => (
          <div
            className={cn(
              "flex flex-1 flex-col gap-2",
              index === 0
                ? "items-center md:items-start"
                : "hidden items-start md:flex",
              index === 1 ? "border-white border-x-[.1rem]" : "",
              brand.isDolomite ? "p-6" : "p-4"
            )}
            key={`footer-cta-${slot.heading}-${index}`}
          >
            <div className="max-h-12 flex-1 py-2">
              <Heading
                className="xs:text-nowrap text-center text-neutral-dark md:text-left"
                hasUnderline={true}
                variant={"h5"}
                wrapperClass={"items-center md:items-start"}
              >
                {slot.heading}
              </Heading>
            </div>
            <div
              className={cn(
                "h-24 flex-1 py-2",
                index === 0 ? "hidden md:block" : ""
              )}
            >
              <Body className="text-left" size="sm">
                {slot.description}
              </Body>
            </div>
            <div className="flex w-full flex-col items-start gap-1 py-2 xl:flex-row">
              {slot.watermarkText && (
                <Input
                  className="mr-1 w-full border-neutral-medium bg-transparent hover:border-neutral-dark xl:w-auto"
                  classNameInput="placeholder:text-neutral-medium"
                  hasIcon={false}
                  id="footer-newsletter-email"
                  name="footer-newsletter-email"
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder={slot.watermarkText}
                  ref={inputRef}
                  size="sm"
                  type={"email"}
                  value={email}
                />
              )}
              <Button
                className="w-full md:w-auto"
                onClick={() => handleSubmit(slot.submitLink || "")}
                size="sm"
                variant="outline"
              >
                {slot.submitLabel}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

FooterCTA.displayName = "FooterCTA";

export { FooterCTA };
